import React, { memo } from 'react';
// import { TouchableOpacity } from 'react-native';
import { Avatar, XStack, Button } from 'tamagui';

import { CobuStack, CobuText } from '@cobu/ui';
import { MemberAvatarProps, Member, GlobalMember, GROUP_MEMBERS_CONSTANTS } from '@cobu/types';
import { URLS } from '../../constants';
import { Strings } from '../../constants/Strings';

/**
 * Utility function to get member display name
 */
function getMemberDisplayName(member: Member | GlobalMember): string {
  if ('user' in member && member.user) {
    return `${member.user.firstName} ${member.user.lastName}`.trim();
  }
  return `${member.firstName || ''} ${member.lastName || ''}`.trim() || Strings.get().ui.members.unknownUser;
}

/**
 * Utility function to get member profile image
 */
function getMemberProfileImage(member: Member | GlobalMember): string | undefined {
  if ('user' in member && member.user) {
    return member.user.profileImage;
  }
  return member.profileImage;
}

/**
 * Utility function to generate initials from name
 */
function getInitials(member: Member | GlobalMember): string {
  const name = getMemberDisplayName(member);
  const names = name.split(' ').filter(Boolean);

  if (names.length === 0) return 'U';
  if (names.length === 1) return names[0][0].toUpperCase();

  return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
}

/**
 * Utility function to check if member is a captain/moderator
 */
function isCaptain(member: Member | GlobalMember): boolean {
  return member.role?.value > 0;
}

const MemberAvatar: React.FC<MemberAvatarProps> = memo(({
  member,
  index,
  size = GROUP_MEMBERS_CONSTANTS.DEFAULT_AVATAR_SIZE,
  onPress,
}) => {
  const profileImage = getMemberProfileImage(member);
  const initials = getInitials(member);
  const isMemberCaptain = isCaptain(member);

  // Calculate avatar size (captains get slightly larger avatars)
  const avatarSize = isMemberCaptain ? size + GROUP_MEMBERS_CONSTANTS.CAPTAIN_SIZE_BONUS : size;

  // Calculate position with overlap effect
  const leftOffset = index * GROUP_MEMBERS_CONSTANTS.OVERLAP_OFFSET;

  // Handle press action
  const handlePress = () => {
    if (onPress) {
      onPress(member);
    }
  };

  return (
    <CobuStack layout='horizontal'
      pos="relative"
      l={leftOffset}
      zi={GROUP_MEMBERS_CONSTANTS.Z_INDEX_BASE + index}
    >
      <Button
        br={avatarSize / 2}
        ai="center"
        jc="center"
        bg="transparent"
        bw={0}
        hoverStyle={{ bg: 'transparent' , bw:0}}
        pressStyle={{ bg: 'transparent' , bw:0}}
        focusStyle={{ outlineWidth: 0 }}
        onPress={onPress ? handlePress : undefined}
        disabled={!onPress}
        pos="relative"
        // activeOpacity={onPress ? GROUP_MEMBERS_CONSTANTS.ACTIVE_OPACITY : 1}
      >
        <CobuStack
          pos="relative"
          style={{
            width: avatarSize,
            height: avatarSize,
            borderRadius: avatarSize / 2,
            borderWidth: 1,
            borderColor: '#FFFFFF',
            borderStyle: 'solid',
            overflow: 'hidden',
            isolation: 'isolate',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Avatar
            circular
            size={avatarSize}
            style={{
              overflow: 'hidden',
              mixBlendMode: 'normal',
            }}
          >
          {profileImage && profileImage !== URLS.DEFAULT_PROFILE_IMAGE ? (
            <Avatar.Image
              source={{ uri: profileImage }}
              width={avatarSize}
              height={avatarSize}
            />
          ) : (
            <Avatar.Fallback
              bg={GROUP_MEMBERS_CONSTANTS.COLORS.AVATAR_FALLBACK.BACKGROUND}
              jc="center"
              ai="center"
              style={{
                opacity: 1,
                mixBlendMode: 'normal',
              }}
            >
              <CobuText
                variant="body"
                color={GROUP_MEMBERS_CONSTANTS.COLORS.AVATAR_FALLBACK.TEXT}
              >
                {initials}
              </CobuText>
            </Avatar.Fallback>
          )}
          </Avatar>
        </CobuStack>
      </Button>
    </CobuStack>
  );
});

MemberAvatar.displayName = 'MemberAvatar';

export default MemberAvatar; 